import React from "react";
import { motion } from "framer-motion";
import { useMicrospecThemeLanguageContext } from "../../../MicrospecDynamicUI/Themes";
import { Box, Typography, Container, Button } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

/* Animated EEG-style brainwave line for CTA background */
const CTAWaveLine = ({ d, color, delay = 0 }) => (
  <motion.path
    d={d}
    fill="none"
    stroke={color}
    strokeWidth="1.5"
    strokeLinecap="round"
    initial={{ pathLength: 0, opacity: 0 }}
    whileInView={{ pathLength: 1, opacity: 0.25 }}
    viewport={{ once: true }}
    transition={{
      pathLength: { duration: 2.5, delay },
      opacity: { duration: 1, delay },
    }}
  />
);

/* Small neural dot cluster decoration */
const NeuralDots = () => {
  const dots = [
    { cx: 50, cy: 40, r: 2.5 },
    { cx: 90, cy: 65, r: 2 },
    { cx: 130, cy: 30, r: 3 },
    { cx: 170, cy: 70, r: 2 },
    { cx: 60, cy: 80, r: 1.5 },
    { cx: 110, cy: 50, r: 2.5 },
    { cx: 150, cy: 85, r: 1.5 },
    { cx: 40, cy: 60, r: 2 },
    { cx: 180, cy: 45, r: 2 },
  ];
  const connections = [
    [0, 1],
    [1, 5],
    [5, 2],
    [2, 3],
    [3, 6],
    [0, 7],
    [7, 4],
    [4, 1],
    [2, 8],
    [8, 3],
  ];

  return (
    <svg
      viewBox="0 0 220 100"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      {connections.map(([a, b], i) => (
        <motion.line
          key={i}
          x1={dots[a].cx}
          y1={dots[a].cy}
          x2={dots[b].cx}
          y2={dots[b].cy}
          stroke="#8B5CF6"
          strokeWidth="0.8"
          strokeOpacity="0.3"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, delay: i * 0.1 }}
        />
      ))}
      {dots.map((dot, i) => (
        <motion.circle
          key={`d-${i}`}
          cx={dot.cx}
          cy={dot.cy}
          r={dot.r}
          fill={i % 2 === 0 ? "#8B5CF6" : "#7393B3"}
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: [0.4, 0.8, 0.4], scale: 1 }}
          viewport={{ once: true }}
          transition={{
            duration: 2.5,
            delay: 0.5 + i * 0.08,
            repeat: Infinity,
          }}
        />
      ))}
    </svg>
  );
};

const CTASection = () => {
  const { localizationProvider, dir } = useMicrospecThemeLanguageContext();

  const title = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.cta.title",
  });
  const subtitle = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.cta.subtitle",
  });
  const button = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.cta.button",
  });

  return (
    <Box className="py-28" id="contact" sx={{ background: "transparent" }}>
      <Container maxWidth="xl" dir={dir}>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="mx-4 md:mx-8"
        >
          <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-[#0A0F2E] via-[#131836] to-[#0F0A20] py-24 px-8 md:px-20 text-center shadow-2xl shadow-[#8B5CF6]/10">
            {/* Enhanced volumetric ambient blobs */}
            <motion.div
              className="absolute top-[-30%] left-[-10%] w-[50%] h-[80%] rounded-full bg-[#7393B3]/18 blur-[120px] pointer-events-none"
              animate={{ opacity: [0.12, 0.22, 0.12] }}
              transition={{ duration: 6, repeat: Infinity }}
            />
            <motion.div
              className="absolute bottom-[-30%] right-[-10%] w-[50%] h-[80%] rounded-full bg-[#8B5CF6]/14 blur-[120px] pointer-events-none"
              animate={{ opacity: [0.1, 0.18, 0.1] }}
              transition={{ duration: 8, repeat: Infinity, delay: 2 }}
            />
            <motion.div
              className="absolute top-[20%] right-[20%] w-[30%] h-[40%] rounded-full bg-[#C084FC]/8 blur-[80px] pointer-events-none"
              animate={{ opacity: [0.05, 0.12, 0.05] }}
              transition={{ duration: 10, repeat: Infinity, delay: 4 }}
            />

            {/* Animated brainwave SVG background — more vivid */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 1200 400"
              preserveAspectRatio="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <CTAWaveLine
                d="M0,200 Q100,200 150,170 T250,130 T350,210 T450,160 T550,230 T650,180 T750,200 T850,150 T950,220 T1050,180 T1200,200"
                color="#7393B3"
                delay={0}
              />
              <CTAWaveLine
                d="M0,250 Q80,250 140,230 T280,200 T380,260 T500,220 T600,270 T720,235 T840,260 T960,210 T1100,250 T1200,240"
                color="#8B5CF6"
                delay={0.4}
              />
              <CTAWaveLine
                d="M0,160 Q120,160 200,140 T320,180 T440,120 T560,170 T680,130 T800,160 T920,140 T1040,170 T1200,155"
                color="#C084FC"
                delay={0.8}
              />
            </svg>

            {/* Neural dot clusters - more visible */}
            <div className="absolute top-4 left-4 w-28 h-16 opacity-50 pointer-events-none">
              <NeuralDots />
            </div>
            <div className="absolute bottom-4 right-4 w-28 h-16 opacity-50 pointer-events-none">
              <NeuralDots />
            </div>

            {/* Grid pattern overlay */}
            <div
              className="absolute inset-0 opacity-[0.03] pointer-events-none"
              style={{
                backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                backgroundSize: "60px 60px",
              }}
            />

            <div className="relative z-10 max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Typography
                  variant="h2"
                  className="text-3xl md:text-5xl font-bold tracking-tight mb-6 leading-tight"
                  sx={{
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #e2e8f0 40%, #C084FC 90%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {title}
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.35 }}
              >
                <Typography className="text-lg text-slate-400 mb-12 leading-relaxed max-w-2xl mx-auto">
                  {subtitle}
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <Button
                  variant="contained"
                  endIcon={
                    <ArrowForwardIosIcon
                      style={{ fontSize: "0.8rem" }}
                      className={dir === "rtl" ? "rotate-180" : ""}
                    />
                  }
                  sx={{
                    background:
                      "linear-gradient(135deg, #7393B3 0%, #8B5CF6 100%)",
                    borderRadius: "9999px",
                    textTransform: "none",
                    px: 5,
                    py: 2,
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    boxShadow: "0 8px 40px rgba(139, 92, 246, 0.4), 0 0 80px rgba(139,92,246,0.15)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5A7A9A 0%, #7C3AED 100%)",
                      boxShadow: "0 12px 50px rgba(139, 92, 246, 0.55), 0 0 100px rgba(139,92,246,0.2)",
                      transform: "translateY(-3px)",
                    },
                  }}
                >
                  {button}
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default CTASection;
